import { createApp } from "vue";
import "@maersk-global/mds-components-core";
import mcModel from "@maersk-global/mds-vue-mc-model";
import App from "./App.vue";
import router from "./router";
import { MaerskVueApp } from "@maersk-global/vue-plugin-maerskvueapp";
import { MaerskGAEventsDispatcher } from "@maersk-global/vue-plugin-gaevent";
import { VueLanguageHandler } from "@maersk-global/vue-plugin-languagehandler";
import GAConfig from "./googleAnalyticsConfig.js";
import { createPinia, PiniaVuePlugin } from "pinia";
import { MdsConfig } from "@maersk-global/mds-config";
import { FeatureFlags } from "@maersk-global/vue-plugin-featureflags";
import CONSTANTS from "@/lib/CONSTANTS";
import { LogLevel } from "@grafana/faro-web-sdk";
import { getCustomerCode, getUserCredential } from "@maersk-global/shared-js";
import { apex } from "../apex";

apex.init();

window.RUM?.RumInit({
  app: {
    name: CONSTANTS.pensieveAppName,
    version: "1.0.0",
    environment: CONSTANTS.pensieveEnv
  },
  sso_user: true,
  apiKey: CONSTANTS.pensieveApiKey,
  debug: true,
  performanceInstrument: {
    ignoreEvents: ["msal.*", "vue.*"]
  },
  traceLog: true,
  instrumentations: {
    documentload: true,
    xhr: true,
    fetch: true,
    console: {
      disabledLevels: [LogLevel.INFO, LogLevel.DEBUG, LogLevel.TRACE, LogLevel.WARN]
    }
  },
  batching: {
    enabled: true
  },
  spanLimitInTrace: 3,
  scheduledDelayInTrace: 2000,
  useSendBeacon: true,
  propagateTraceHeaderCorsUrls: [new RegExp(".*/api/synergy/tracking/.*")],
  ignoreUrls: ["https://www.google-analytics.com/*", "https://app.launchdarkly.com/*"]
});

window.faro?.api?.setUser({
  id: getUserCredential("personid"),
  attributes: {
    customerCode: getCustomerCode()
  }
});

const app = createApp(App);
const pinia = createPinia();
app.directive("mc-model", mcModel);
MdsConfig.iconsDynamicImportPath = "../../";
if (import.meta.env.PROD) {
  // If your production path differs from development
  MdsConfig.iconsDynamicImportPath = "./";
}
pinia.use(({ store }) => {
  store.$sendGAEvent = app.config.globalProperties.$sendGAEvent;
});

app.config.ignoredElements = [/^mc-/];

const config = {};
const featureFlagConfig = {}; // extends or overrides the feature flag configuration

config.multiBranding = {
  maeuCSS: () => import("@/scss/maeu.scss"),
  seauCSS: () => import("@/scss/seau.scss")
};

if (process.env.NODE_ENV === "production") {
  app.config.devtools = false;
  app.config.debug = false;
  app.config.silent = true;
} else if (process.env.NODE_ENV === "development") {
  // These parameters are only used in localhost
  // Local vue config
  app.config.devtools = true;
  app.config.performance = true;
  //This is a switch to the branding
  config.brandParam = "maeu"; // values: 'seau', 'safm', 'maeu' or 'maei';
}

// Language handling.
if (import.meta.env.DEV) {
  MdsConfig.iconsDynamicImportPath = "../../";
  // config.designTokenRootPath = "../dist";
  // In localhost mode, always use the local environment
  app.use(VueLanguageHandler, {
    langFilePathWeb: "/locales",
    // keyRootId: "4CA73DED-9C9A-4ADC-B985-1978A70BBAE5", // Uncomment this to test remote keys in dev
    langFilePathLocal: "/locales",
    doubleDelimiter: true
  });
} else {
  // In production mode, always use Sitecore
  app.use(VueLanguageHandler, {
    langFilePathWeb: "",
    keyRootId: "4CA73DED-9C9A-4ADC-B985-1978A70BBAE5",
    langFilePathLocal: "/locales",
    doubleDelimiter: true,
    keyRootVersion: "0"
  });
}

app.config.productionTip = false;

app.use(FeatureFlags, featureFlagConfig);
app.use(MaerskGAEventsDispatcher, GAConfig);
app.use(MaerskVueApp, config);
app.use(pinia);
app.use(PiniaVuePlugin);
app.use(router);

VueLanguageHandler.loadLanguageData().then(() => {
  app.mount("#maersk_app");
});
